import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  createFeatureFlagClient,
  type FeatureFlagClient,
  type FeatureFlagClientFactory,
  type FeatureFlagSnapshot,
} from './client'
import {
  parseFeatureFlagOverrides,
  resolveConfigCatSdkKey,
  type FeatureFlagOverrides,
} from './config'
import { evaluateFeatureFlag, takeFeatureFlagSnapshot } from './evaluate'
import { FEATURE_FLAG_KEYS, type FeatureFlagKey, type FeatureFlagUser } from './registry'
import { FeatureFlagsContext, type FeatureFlagValues } from './useFeatureFlag'

type Runtime = Readonly<{
  client?: FeatureFlagClient
  overrides: FeatureFlagOverrides
}>

type FeatureFlagsProviderProps = Readonly<{
  children: ReactNode
  clientFactory?: FeatureFlagClientFactory
  user?: FeatureFlagUser
}>

function evaluateAll(
  snapshot: FeatureFlagSnapshot | undefined,
  overrides: FeatureFlagOverrides,
  user?: FeatureFlagUser,
): FeatureFlagValues {
  const values = {} as Record<FeatureFlagKey, boolean>
  for (const key of FEATURE_FLAG_KEYS) {
    values[key] = evaluateFeatureFlag(key, snapshot, overrides, user).value
  }
  return values
}

function createRuntime(clientFactory: FeatureFlagClientFactory): Runtime {
  const sdkKey = resolveConfigCatSdkKey(import.meta.env.VITE_CONFIGCAT_SDK_KEY)
  let client: FeatureFlagClient | undefined
  if (sdkKey !== undefined) {
    try {
      client = clientFactory(sdkKey)
    } catch {
      if (import.meta.env.DEV) {
        console.warn('ConfigCat client could not be initialized.')
      }
    }
  }
  const overrides =
    sdkKey === undefined && import.meta.env.DEV
      ? parseFeatureFlagOverrides(import.meta.env.VITE_FEATURE_FLAG_OVERRIDES, (message) =>
          console.warn(message),
        )
      : {}
  return { ...(client !== undefined ? { client } : {}), overrides }
}

export function FeatureFlagsProvider({
  children,
  clientFactory = createFeatureFlagClient,
  user,
}: FeatureFlagsProviderProps) {
  const [runtime] = useState(() => createRuntime(clientFactory))
  const [snapshot, setSnapshot] = useState(() => takeFeatureFlagSnapshot(runtime.client))
  const selectedUserRef = useRef(user)
  const loggedSample = useRef(false)
  const values = useMemo(
    () => evaluateAll(snapshot, runtime.overrides, user),
    [snapshot, runtime, user],
  )
  const contextValue = useMemo(() => ({ values }), [values])

  useEffect(() => {
    selectedUserRef.current = user
  }, [user])

  useEffect(() => {
    const { client } = runtime
    const logSample = () => {
      if (!import.meta.env.DEV || loggedSample.current) {
        return
      }
      loggedSample.current = true
      const evaluation = evaluateFeatureFlag(
        'exemploFlag',
        takeFeatureFlagSnapshot(runtime.client),
        runtime.overrides,
        selectedUserRef.current,
      )
      console.info('ConfigCat feature flag', {
        key: 'exemploFlag',
        value: evaluation.value,
        source: evaluation.source,
      })
    }

    if (client === undefined) {
      logSample()
      return
    }

    let active = true
    const handleConfigChanged = () => {
      if (active) {
        setSnapshot(takeFeatureFlagSnapshot(client))
      }
    }
    client.on('configChanged', handleConfigChanged)

    void client.waitForReady().then(() => {
      if (!active) {
        return
      }
      setSnapshot(takeFeatureFlagSnapshot(client))
      logSample()
    })

    return () => {
      active = false
      client.off('configChanged', handleConfigChanged)
    }
  }, [runtime])

  return (
    <FeatureFlagsContext.Provider value={contextValue}>{children}</FeatureFlagsContext.Provider>
  )
}
